import { motion } from "framer-motion";
import { Flame, Target, Hexagon, TrendingUp, Brain, Lightbulb, Sparkles } from "lucide-react";
import MockAdCreative from "./MockAdCreative";

const features = [
  {
    icon: Flame,
    title: "Attention Heatmaps",
    desc: "See exactly where your audience will look. Our AI generates eye-tracking-grade attention maps powered by saliency prediction models trained on real eye-tracking data.",
    visual: "heatmap",
  },
  {
    icon: Target,
    title: "Brand Attention Score",
    desc: "Is your logo actually getting noticed? We detect your brand elements and measure what percentage of visual attention they capture vs. competing elements.",
    visual: "gauge",
  },
  {
    icon: Hexagon,
    title: "Ad Neuro Score",
    desc: "One unified score (0-100) combining 6 neuroscience dimensions: Attention, Cognitive Clarity, Emotional Connection, Brand Visibility, Memorability, and Call-to-Action effectiveness.",
    visual: "radar",
  },
  {
    icon: TrendingUp,
    title: "Engagement Prediction",
    desc: "Predict social media engagement before posting. Our models analyze visual, copy, and structural elements correlated with high-performing content.",
    visual: "prediction",
  },
  {
    icon: Brain,
    title: "Memorability Score",
    desc: "Will people remember your ad tomorrow? We measure visual distinctiveness, emotional peaks, and message simplicity to predict long-term recall.",
    visual: "memory",
  },
  {
    icon: Lightbulb,
    title: "AI Optimization Recommendations",
    desc: "Don't just see problems \u2014 get solutions. Receive specific, neuroscience-backed suggestions: reposition elements, reduce cognitive load, improve CTA visibility, and more.",
    visual: "recommendation",
  },
];

function FeatureVisual({ type }) {
  if (type === "heatmap") {
    return (
      <div className="relative w-full h-24 rounded-lg overflow-hidden">
        <MockAdCreative showHeatmap={true} size="sm" />
      </div>
    );
  }
  if (type === "gauge") {
    return (
      <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
        <div className="relative w-12 h-12">
          <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
            <circle cx="18" cy="18" r="15" fill="none" stroke="#E2E8F0" strokeWidth="3" />
            <circle cx="18" cy="18" r="15" fill="none" stroke="#F97316" strokeWidth="3" strokeDasharray="94.2" strokeDashoffset="25.4" strokeLinecap="round" />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-orange-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>73</span>
        </div>
        <div>
          <p className="text-[10px] text-slate-400">Brand Visibility</p>
          <p className="text-sm font-bold text-orange-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>73/100</p>
        </div>
      </div>
    );
  }
  if (type === "radar") {
    const dims = ["ATT", "CLR", "EMO", "BRD", "MEM", "CTA"];
    return (
      <div className="flex items-center justify-center bg-slate-50 rounded-lg p-3">
        <svg viewBox="0 0 120 120" className="w-24 h-24">
          {/* Hexagon outlines */}
          {[1, 0.66, 0.33].map((scale, si) => (
            <polygon
              key={si}
              points={getHexPoints(60, 60, 45 * scale)}
              fill="none"
              stroke="#E2E8F0"
              strokeWidth="0.5"
            />
          ))}
          {/* Data polygon */}
          <polygon
            points={getDataPoints(60, 60, 45, [0.87, 0.72, 0.81, 0.62, 0.75, 0.9])}
            fill="rgba(249, 115, 22, 0.15)"
            stroke="#F97316"
            strokeWidth="1.5"
          />
          {/* Labels */}
          {dims.map((d, i) => {
            const angle = (Math.PI * 2 * i) / 6 - Math.PI / 2;
            const x = 60 + Math.cos(angle) * 54;
            const y = 60 + Math.sin(angle) * 54;
            return (
              <text key={d} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fontSize="5" fill="#94A3B8" fontFamily="JetBrains Mono, monospace">
                {d}
              </text>
            );
          })}
        </svg>
      </div>
    );
  }
  if (type === "prediction") {
    return (
      <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-xs font-bold text-emerald-700" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Predicted: HIGH</span>
        </div>
        <p className="text-[10px] text-emerald-600">Top 20% for this category</p>
      </div>
    );
  }
  if (type === "memory") {
    return (
      <div className="bg-slate-50 rounded-lg p-3">
        <svg viewBox="0 0 200 60" className="w-full h-10">
          <path d="M 10 15 Q 50 15, 70 20 T 130 35 T 190 50" fill="none" stroke="#E2E8F0" strokeWidth="2" />
          <path d="M 10 15 Q 50 16, 70 22 T 130 30 T 190 38" fill="none" stroke="#F97316" strokeWidth="2" strokeDasharray="4 2" />
          <text x="10" y="55" fontSize="6" fill="#94A3B8" fontFamily="JetBrains Mono, monospace">Day 1</text>
          <text x="175" y="55" fontSize="6" fill="#94A3B8" fontFamily="JetBrains Mono, monospace">Day 7</text>
        </svg>
        <p className="text-[10px] text-slate-500 mt-1">Memory retention curve</p>
      </div>
    );
  }
  if (type === "recommendation") {
    return (
      <div className="bg-orange-50 border border-orange-100 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <div className="w-4 h-4 rounded-full bg-orange-200 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Lightbulb size={8} className="text-orange-600" />
          </div>
          <p className="text-[10px] text-orange-700 leading-relaxed">
            Move logo to lower-right zone to increase brand recall by ~25%
          </p>
        </div>
      </div>
    );
  }
  if (type === "optimize") {
    return (
      <div className="grid grid-cols-2 gap-2 items-center">
        <div className="relative bg-slate-100 rounded-lg p-2 aspect-square flex items-center justify-center">
          <div className="text-[8px] text-slate-400 absolute top-1 left-2 font-bold">BEFORE</div>
          <div className="w-full h-full bg-white rounded flex flex-col items-center justify-center gap-1 p-2">
            <div className="w-full h-1.5 bg-slate-200 rounded" />
            <div className="w-2/3 h-1.5 bg-slate-200 rounded" />
            <div className="w-12 h-3 bg-slate-300 rounded mt-1" />
          </div>
        </div>
        <div className="relative rounded-lg p-2 aspect-square flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #FAE8FF 100%)' }}>
          <div className="text-[8px] absolute top-1 left-2 font-bold" style={{ color: '#F97316' }}>AFTER ✨</div>
          <div className="w-full h-full bg-white rounded flex flex-col items-center justify-center gap-1 p-2">
            <div className="w-full h-2 rounded" style={{ background: 'linear-gradient(90deg, #F97316, #D946EF)' }} />
            <div className="w-3/4 h-1.5 bg-slate-300 rounded" />
            <div className="w-14 h-3.5 rounded mt-1" style={{ background: 'linear-gradient(135deg, #F97316, #D946EF)' }} />
          </div>
        </div>
      </div>
    );
  }
  return null;
}

function getHexPoints(cx, cy, r) {
  return Array.from({ length: 6 }, (_, i) => {
    const angle = (Math.PI * 2 * i) / 6 - Math.PI / 2;
    return `${cx + Math.cos(angle) * r},${cy + Math.sin(angle) * r}`;
  }).join(" ");
}

function getDataPoints(cx, cy, r, values) {
  return values
    .map((v, i) => {
      const angle = (Math.PI * 2 * i) / 6 - Math.PI / 2;
      return `${cx + Math.cos(angle) * r * v},${cy + Math.sin(angle) * r * v}`;
    })
    .join(" ");
}

export default function FeaturesSection() {
  return (
    <section id="features" className="py-24 bg-[#F8F9FC]" data-testid="features-section">
      <div className="max-w-[1200px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#0F172A' }}>
            Everything You Need to <span className="gradient-text">Predict Ad Success</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`feature-card-${i}`}
              className="bg-white rounded-2xl border border-slate-200 p-6 hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-500/10 transition-all duration-300 flex flex-col"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                  <f.icon size={20} className="text-orange-500" />
                </div>
                {f.badge && (
                  <span className="text-[10px] font-bold px-2 py-1 rounded-md text-white" style={{ background: 'linear-gradient(135deg, #F97316 0%, #D946EF 100%)' }}>
                    {f.badge}
                  </span>
                )}
              </div>
              <h3 className="text-base font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: '#0F172A' }}>
                {f.title}
              </h3>
              <p className="text-sm leading-relaxed mb-4 flex-1" style={{ color: '#475569' }}>
                {f.desc}
              </p>
              <FeatureVisual type={f.visual} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
